import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { ApiResponse, IGroup } from "../../../types";

type GetGroupsArg =
  | void
  | string
  | {
      groupId?: string;
      page?: number;
      limit?: number;
    };

export const groupApi = createApi({
  reducerPath: "groupApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Groups"],

  endpoints: (builder) => ({
    getGroups: builder.query<IGroup[], GetGroupsArg>({
      query: (arg) => {
        let groupId: string | undefined;
        let page: number | undefined;
        let limit: number | undefined;

        if (typeof arg === "string") {
          groupId = arg;
        } else if (typeof arg === "object" && arg !== null) {
          groupId = arg.groupId;
          page = arg.page;
          limit = arg.limit;
        }

        const baseUrl =
          groupId && groupId !== ""
            ? `${ApiEndpoints.getAllGroups}/${groupId}`
            : ApiEndpoints.group;

        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (limit !== undefined) {
          params.append("limit", String(limit));
        } else {
          params.append("limit", "-1");
        }

        return {
          url: `${baseUrl}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<IGroup[]>) => {
        return res?.result ?? res?.results ?? [];
      },
      providesTags: ["Groups"],
    }),

    getGroupById: builder.query<IGroup, string>({
      query: (id) => ({
        url: `${ApiEndpoints.group}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: ApiResponse<IGroup>) => {
        return res?.result!;
      },
      providesTags: (_result, _error, id) => [{ type: "Groups", id }],
    }),

    createGroup: builder.mutation<ApiResponse<IGroup>, Partial<IGroup>>({
      query: (data) => ({
        url: ApiEndpoints.createGroup,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Groups"],
    }),

    editGroup: builder.mutation<
      ApiResponse<IGroup>,
      { id: string; data: Partial<IGroup> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editGroup}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Groups",
        { type: "Groups", id },
      ],
    }),

    deleteGroup: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteGroup}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Groups"],
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useCreateGroupMutation,
  useEditGroupMutation,
  useDeleteGroupMutation,
} = groupApi;
